import { watch } from 'vue';
import i18n from '@/i18n';
import uiCopy from './ui-copy.en.json';
import uiPatterns from './ui-patterns.en.json';

const TEXT_ORIGINAL = new WeakMap();
const ATTRIBUTE_ORIGINAL = new WeakMap();
const ATTRIBUTES = ['title', 'placeholder', 'aria-label', 'alt'];
const SKIP_SELECTOR = 'script,style,noscript,textarea,pre,code,[data-ui-copy="preserve"]';
const hasChinese = (value) => /[\u3400-\u9fff]/.test(value);
const escapePattern = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const dynamicPatterns = Object.entries(uiPatterns).map(([source, target]) => {
  const captures = [];
  const markerSource = source.replace(/\{(\d+)\}/g, (_, index) => { captures.push(Number(index)); return `___UI_CAPTURE_${captures.length - 1}___`; });
  let expression = escapePattern(markerSource).replace(/___UI_CAPTURE_(\d+)___/g, '(.+?)');
  expression = expression.replace(/\\ /g, '\\s*');
  return { expression: new RegExp(`^${expression}$`), target, captures, sourceParts: source.split(/\{\d+\}/), targetParts: target.split(/\{\d+\}/) };
});

function translateValue(value) {
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  const source = value.trim().replace(/\s+/g, ' ');
  if (uiCopy[source]) return `${leading}${uiCopy[source]}${trailing}`;
  for (const pattern of dynamicPatterns) {
    const match = source.match(pattern.expression);
    if (!match) continue;
    const translated = pattern.target.replace(/\{(\d+)\}/g, (_, index) => {
      const captureIndex = pattern.captures.indexOf(Number(index));
      return captureIndex >= 0 ? match[captureIndex + 1] : '';
    });
    return `${leading}${translated}${trailing}`;
  }
  for (const pattern of dynamicPatterns) {
    const prefix = pattern.sourceParts[0];
    if (prefix && source.startsWith(prefix) && source.length > prefix.length) {
      return `${leading}${pattern.targetParts[0]}${source.slice(prefix.length)}${trailing}`;
    }
    const suffix = pattern.sourceParts.at(-1);
    if (suffix && source.endsWith(suffix) && source.length > suffix.length) {
      return `${leading}${source.slice(0, -suffix.length)}${pattern.targetParts.at(-1)}${trailing}`;
    }
  }
  return value;
}

function shouldSkip(node) {
  return node.parentElement?.closest(SKIP_SELECTOR);
}

function translateText(node, english) {
  if (shouldSkip(node)) return;
  if (!TEXT_ORIGINAL.has(node) && hasChinese(node.nodeValue || '')) TEXT_ORIGINAL.set(node, node.nodeValue);
  let source = TEXT_ORIGINAL.get(node);
  if (source === undefined) return;
  const translated = translateValue(source);
  if (node.nodeValue !== source && node.nodeValue !== translated) {
    if (!hasChinese(node.nodeValue || '')) { TEXT_ORIGINAL.delete(node); return; }
    source = node.nodeValue;
    TEXT_ORIGINAL.set(node, source);
  }
  const next = english ? translateValue(source) : source;
  if (node.nodeValue !== next) node.nodeValue = next;
}

function translateAttributes(element, english) {
  if (element.closest(SKIP_SELECTOR)) return;
  let originals = ATTRIBUTE_ORIGINAL.get(element);
  for (const attribute of ATTRIBUTES) {
    const current = element.getAttribute(attribute);
    if (!current) continue;
    if (!originals && hasChinese(current)) {
      originals = {};
      ATTRIBUTE_ORIGINAL.set(element, originals);
    }
    if (originals && originals[attribute] === undefined && hasChinese(current)) originals[attribute] = current;
    const source = originals?.[attribute];
    if (source === undefined) continue;
    const translated = translateValue(source);
    let latestSource = source;
    if (current !== source && current !== translated) {
      if (!hasChinese(current)) { delete originals[attribute]; continue; }
      latestSource = current;
      originals[attribute] = current;
    }
    const next = english ? translateValue(latestSource) : latestSource;
    if (current !== next) element.setAttribute(attribute, next);
  }
}

function apply(root = document.querySelector('#app')) {
  if (!root) return;
  const english = i18n.global.locale.value === 'en';
  if (root.nodeType === Node.TEXT_NODE) translateText(root, english);
  if (root.nodeType === Node.ELEMENT_NODE) translateAttributes(root, english);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    if (walker.currentNode.nodeType === Node.TEXT_NODE) translateText(walker.currentNode, english);
    else translateAttributes(walker.currentNode, english);
  }
}

export function installUiLocalization() {
  const root = document.querySelector('#app');
  if (!root) return () => {};
  let queued = false;
  const pending = new Set();
  const schedule = (target = root) => {
    pending.add(target);
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      const targets = [...pending];
      pending.clear();
      targets.forEach((item) => apply(item));
    });
  };
  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'childList') record.addedNodes.forEach(schedule);
      else schedule(record.target);
    });
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTES });
  const stop = watch(i18n.global.locale, () => schedule(root), { immediate: true });
  return () => { stop(); observer.disconnect(); };
}
